import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  Package, 
  FileText, 
  Check, 
  Edit,
  Trash2, 
  AlertCircle, 
  Save, 
  Plus,
  ArrowLeft,
  Send,
  Upload,
  X,
  Camera,
  Eye,
  PlusCircle,
  Image,
  ImagePlus,
  Calendar
} from 'lucide-react';
import { reportsAPI, timeEntriesAPI, materialUsagesAPI, materialsAPI } from '../services/api';

const ReportDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit-States
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [newTimeEntry, setNewTimeEntry] = useState(null);
  const [newMaterial, setNewMaterial] = useState(null);
  
  const [images, setImages] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Neuer State für den Reparaturstatus
  const [repairCompleted, setRepairCompleted] = useState(null);
  
  // Daten laden
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Report laden
        const reportResponse = await reportsAPI.getById(id);
        const reportData = reportResponse.data.report;
        setReport(reportData);
        
        // Reparaturstatus setzen, kann null, true oder false sein
        setRepairCompleted(reportData.repair_completed);
        
        // Zeiteinträge laden
        const timeResponse = await timeEntriesAPI.getByProject(reportData.project_id);
        setTimeEntries(timeResponse.data.timeEntries || []);
        
        // Materialeinträge laden
        const materialsResponse = await materialUsagesAPI.getByProject(reportData.project_id);
        setMaterials(materialsResponse.data.materialUsages || []);
        
        // Verfügbare Materialien für Auswahl laden
        const availableMaterialsResponse = await materialsAPI.getAll();
        setAvailableMaterials(availableMaterialsResponse.data.materials || []);
        
        // Bilder für den Rapport laden
        const imagesResponse = await fetch(`${process.env.REACT_APP_API_URL}/reports/${id}/images`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!imagesResponse.ok) {
          throw new Error('Fehler beim Laden der Bilder');
        }
        
        const imagesData = await imagesResponse.json();
        setImages(imagesData.images || []);
        
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError('Fehler beim Laden der Daten: ' + 
                (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchData();
    }
  }, [id]);
  
  // Formatierungshilfsfunktionen
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      // Direkte Extraktion des Datums aus dem ISO-String ohne Zeitzonenumwandlung
      const datePart = dateString.split('T')[0]; // Format: YYYY-MM-DD
      if (datePart) {
        const [year, month, day] = datePart.split('-');
        return `${day}.${month}.${year}`;
      }
    } catch (e) {
      console.error('Fehler beim Formatieren des Datums:', e);
    }
    
    // Fallback zur alten Methode, falls etwas schiefgeht
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '-';
    
    try {
      // Direkte Extraktion der Zeit aus dem ISO-String ohne Zeitzonenumwandlung
      const timePart = dateString.split('T')[1];
      if (timePart) {
        // Format: HH:MM:SS -> HH:MM
        return timePart.substr(0, 5);
      }
    } catch (e) {
      console.error('Fehler beim Formatieren der Zeit:', e);
    }
    
    // Fallback zur alten Methode, falls etwas schiefgeht
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  const formatCurrency = (amount) => {
    if (!amount) return '0,00 CHF';
    return parseFloat(amount).toFixed(2).replace('.', ',') + ' CHF';
  };
  
  const calculateHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    
    try {
      // Wir stellen sicher, dass die Zeiten ohne 'Z' sind, um Zeitzonenprobleme zu vermeiden
      let start, end;
      
      if (typeof startTime === 'string') {
        if (startTime.indexOf('Z') !== -1) {
          start = new Date(startTime.replace('Z', ''));
        } else {
          start = new Date(startTime);
        }
      } else {
        start = new Date(startTime);
      }
      
      if (typeof endTime === 'string') {
        if (endTime.indexOf('Z') !== -1) {
          end = new Date(endTime.replace('Z', ''));
        } else {
          end = new Date(endTime);
        }
      } else {
        end = new Date(endTime);
      }
      
      const diffInMs = end - start;
      return (diffInMs / (1000 * 60 * 60)).toFixed(2);
    } catch (e) {
      console.error('Fehler bei der Stundenberechnung:', e);
      // Fallback zur alten Methode
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffInMs = end - start;
      return (diffInMs / (1000 * 60 * 60)).toFixed(2);
    }
  };
  
  // Zeiterfassung bearbeiten
  const handleEditTimeEntry = (entry) => {
    try {
      let startTimeValue, endTimeValue;
      
      // Versuchen, direkt aus dem ISO-String zu extrahieren
      if (entry.start_time) {
        // ISO-Format ohne Z: YYYY-MM-DDTHH:MM:SS
        startTimeValue = entry.start_time.split('.')[0]; // Entferne Millisekunden falls vorhanden
        if (startTimeValue.endsWith('Z')) {
          startTimeValue = startTimeValue.substring(0, startTimeValue.length - 1);
        }
      }
      
      if (entry.end_time) {
        endTimeValue = entry.end_time.split('.')[0]; // Entferne Millisekunden falls vorhanden
        if (endTimeValue.endsWith('Z')) {
          endTimeValue = endTimeValue.substring(0, endTimeValue.length - 1);
        }
      }
      
      setEditingTimeEntry({
        ...entry,
        start_time: startTimeValue || (entry.start_time ? new Date(entry.start_time).toISOString().slice(0, 16) : ''),
        end_time: endTimeValue || (entry.end_time ? new Date(entry.end_time).toISOString().slice(0, 16) : '')
      });
    } catch (e) {
      console.error('Fehler bei handleEditTimeEntry:', e);
      
      // Fallback zur alten Methode
      setEditingTimeEntry({
        ...entry,
        start_time: entry.start_time ? new Date(entry.start_time).toISOString().slice(0, 16) : '',
        end_time: entry.end_time ? new Date(entry.end_time).toISOString().slice(0, 16) : ''
      });
    }
  };
  
  const handleCancelEditTimeEntry = () => {
    setEditingTimeEntry(null);
  };
  
  const handleSaveTimeEntry = async () => {
    try {
      // Formatiere die Zeiten richtig ohne Z-Suffix, um Zeitzonenproblem zu vermeiden
      const formattedEntry = {
        ...editingTimeEntry,
        start_time: editingTimeEntry.start_time ? new Date(editingTimeEntry.start_time).toISOString().replace('Z', '') : null,
        end_time: editingTimeEntry.end_time ? new Date(editingTimeEntry.end_time).toISOString().replace('Z', '') : null
      };
      
      await reportsAPI.updateTimeEntries(id, { 
        timeEntries: [formattedEntry] 
      });
      
      // Daten neu laden
      const timeResponse = await timeEntriesAPI.getByProject(report.project_id);
      setTimeEntries(timeResponse.data.timeEntries || []);
      
      setEditingTimeEntry(null);
      setSuccess('Zeiterfassung erfolgreich aktualisiert');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern der Zeiterfassung: ' + 
              (err.response?.data?.error || err.message));
    }
  };
  
  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Möchten Sie diesen Zeiteintrag wirklich löschen?')) {
      return;
    }
    
    try {
      await reportsAPI.updateTimeEntries(id, { 
        timeEntries: [{ delete_id: entryId }] 
      });
      
      // Daten neu laden
      const timeResponse = await timeEntriesAPI.getByProject(report.project_id);
      setTimeEntries(timeResponse.data.timeEntries || []);
      
      setSuccess('Zeiterfassung erfolgreich gelöscht');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Löschen der Zeiterfassung: ' + 
              (err.response?.data?.error || err.message));
    }
  };
  
  const handleAddTimeEntry = () => {
    setNewTimeEntry({
      description: '',
      start_time: new Date().toISOString().slice(0, 16),
      end_time: new Date().toISOString().slice(0, 16),
      employee_count: 1,
      travel_allowance: false
    });
  };
  
  const handleSaveNewTimeEntry = async () => {
    try {
      // Formatiere die Zeiten richtig ohne Z-Suffix, um Zeitzonenproblem zu vermeiden
      const formattedEntry = {
        ...newTimeEntry,
        start_time: newTimeEntry.start_time ? new Date(newTimeEntry.start_time).toISOString().replace('Z', '') : null,
        end_time: newTimeEntry.end_time ? new Date(newTimeEntry.end_time).toISOString().replace('Z', '') : null
      };
      
      await reportsAPI.updateTimeEntries(id, { 
        timeEntries: [formattedEntry] 
      });
      
      // Daten neu laden
      const timeResponse = await timeEntriesAPI.getByProject(report.project_id);
      setTimeEntries(timeResponse.data.timeEntries || []);
      
      setNewTimeEntry(null);
      setSuccess('Zeiterfassung erfolgreich hinzugefügt');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Hinzufügen der Zeiterfassung: ' + 
              (err.response?.data?.error || err.message));
    }
  };
  
  // Material bearbeiten
  const handleEditMaterial = (material) => {
    setEditingMaterial({
      ...material
    });
  };
  
  const handleCancelEditMaterial = () => {
    setEditingMaterial(null);
  };
  
  const handleSaveMaterial = async () => {
    try {
      await reportsAPI.updateMaterials(id, { 
        materials: [editingMaterial] 
      });
      
      // Daten neu laden
      const materialsResponse = await materialUsagesAPI.getByProject(report.project_id);
      setMaterials(materialsResponse.data.materialUsages || []);
      
      setEditingMaterial(null);
      setSuccess('Material erfolgreich aktualisiert');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern des Materials: ' + 
              (err.response?.data?.error || err.message));
    }
  };
  
  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Möchten Sie diesen Materialeintrag wirklich löschen?')) {
      return;
    }
    
    try {
      await reportsAPI.updateMaterials(id, { 
        materials: [{ delete_id: materialId }] 
      });
      
      // Daten neu laden
      const materialsResponse = await materialUsagesAPI.getByProject(report.project_id);
      setMaterials(materialsResponse.data.materialUsages || []);
      
      setSuccess('Material erfolgreich gelöscht');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Löschen des Materials: ' + 
              (err.response?.data?.error || err.message));
    }
  };
  
  const handleAddMaterial = () => {
    setNewMaterial({
      material_id: '',
      quantity: 1,
      description: ''
    });
  };
  
  const handleSaveNewMaterial = async () => {
    try {
      await reportsAPI.updateMaterials(id, { 
        materials: [newMaterial] 
      });
      
      // Daten neu laden
      const materialsResponse = await materialUsagesAPI.getByProject(report.project_id);
      setMaterials(materialsResponse.data.materialUsages || []);
      
      setNewMaterial(null);
      setSuccess('Material erfolgreich hinzugefügt');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Hinzufügen des Materials: ' + 
              (err.response?.data?.error || err.message));
    }
  };
  
  // Bilder-Sektion
  const loadImages = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/${id}/images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Bilder');
      }
      
      const data = await response.json();
      setImages(data.images || []);
    } catch (error) {
      console.error('Fehler beim Laden der Bilder:', error);
    }
  }, [id]);
  
  useEffect(() => {
    loadImages();
  }, [loadImages]);
  
  // Bild-Upload-Logik
  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    setUploadError('');
    
    console.log(`Upload gestartet: ${files.length} Dateien ausgewählt`);
    
    try {
      // Erweiterte Deduplizierung: Duplikate im Frontend entfernen
      // Anstatt FormData direkt zu erstellen, zuerst Duplikate filtern
      const uniqueFiles = [];
      const fileHashes = new Set();
      
      // Für jede Datei einen Hash aus Name+Größe+Zeitstempel erstellen
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileHash = `${file.name}-${file.size}-${file.lastModified}`;
        
        if (!fileHashes.has(fileHash)) {
          fileHashes.add(fileHash);
          uniqueFiles.push(file);
          console.log(`Eindeutige Datei erkannt: ${file.name} (${fileHash})`);
        } else {
          console.log(`Duplikat übersprungen: ${file.name} (${fileHash})`);
        }
      }
      
      console.log(`Nach Deduplizierung: ${uniqueFiles.length} von ${files.length} Dateien übrig`);
      
      if (uniqueFiles.length === 0) {
        setUploadError('Alle ausgewählten Bilder sind bereits vorhanden.');
        setIsUploading(false);
        return;
      }
      
      // Vorschau für eindeutige Dateien
      const newUploadedImages = uniqueFiles.map(file => ({
        id: Date.now() + Math.random(),
        file,
        url: URL.createObjectURL(file),
        description: '',
        isUploading: true
      }));
      
      setUploadedImages([...uploadedImages, ...newUploadedImages]);
      
      // FormData nur mit eindeutigen Dateien erstellen
      const formData = new FormData();
      
      // Beim Hinzufügen zu FormData einen eindeutigen Dateinamen erzwingen
      uniqueFiles.forEach((file, index) => {
        // Originaldatei mit Zeitstempel versehen, um Duplikate zu vermeiden
        const modifiedFile = new File(
          [file], 
          `${Date.now()}-${index}-${file.name}`, 
          { type: file.type }
        );
        formData.append('images', modifiedFile);
      });
      
      // Beschreibung aus dem Formular hinzufügen, falls vorhanden
      if (event.target.form && event.target.form.description) {
        formData.append('description', event.target.form.description.value);
      }
      
      // Hochladen der Bilder
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/${id}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Hochladen der Bilder');
      }
      
      // Alten Input löschen, um doppelte Hochlade-Ereignisse zu vermeiden
      if (event.target && event.target.value) {
        event.target.value = '';
      }
      
      // Bilder neu laden, um die hochgeladenen anzuzeigen
      loadImages();
      
      // Uploadstatus zurücksetzen
      setUploadedImages([]);
      
      // Formular zurücksetzen
      if (event.target.form) {
        event.target.form.reset();
      }
      
      // Erfolgsmeldung
      alert('Bilder erfolgreich hochgeladen');
    } catch (error) {
      console.error('Fehler beim Hochladen der Bilder:', error);
      setUploadError(error.message || 'Fehler beim Hochladen der Bilder');
    } finally {
      setIsUploading(false);
    }
  };
  
  // Bild-Beschreibung bearbeiten
  const handleUpdateDescription = async (imageId, description) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/${id}/images/${imageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ description })
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Aktualisieren der Beschreibung');
      }
      
      // Bilder neu laden
      loadImages();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Beschreibung:', error);
      alert('Fehler beim Aktualisieren der Beschreibung');
    }
  };
  
  // Bild löschen
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Möchten Sie dieses Bild wirklich löschen?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reports/${id}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Löschen des Bildes');
      }
      
      // Bilder neu laden
      loadImages();
    } catch (error) {
      console.error('Fehler beim Löschen des Bildes:', error);
      alert('Fehler beim Löschen des Bildes');
    }
  };
  
  // Kamera öffnen für direkte Bildaufnahme
  const openCamera = () => {
    // Alter Input löschen, falls vorhanden
    if (window.tempCameraInput) {
      document.body.removeChild(window.tempCameraInput);
      window.tempCameraInput = null;
    }
    
    // Erstelle ein temporäres Eingabeelement mit capture-Attribut für die Kamera
    const tempInput = document.createElement('input');
    tempInput.type = 'file';
    tempInput.accept = 'image/*';
    tempInput.capture = 'environment'; // Nutze die Rückkamera
    tempInput.style.display = 'none';
    
    // Speichere den Input global, damit er nicht vom Garbage Collector entfernt wird
    window.tempCameraInput = tempInput;
    document.body.appendChild(tempInput);
    
    // Eindeutige ID für diesen Kamera-Aufruf
    const cameraSessionId = Date.now();
    console.log(`Neue Kamera-Sitzung gestartet: ${cameraSessionId}`);
    
    // Handler für Dateiauswahl hinzufügen
    tempInput.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        console.log(`Bild aufgenommen in Sitzung ${cameraSessionId}`);
        
        // Eindeutige Namen für Kamera-Bilder erzwingen
        const files = e.target.files;
        const newFileList = new DataTransfer();
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          // Neuen Dateinamen mit Zeitstempel generieren
          const uniqueFileName = `camera-${cameraSessionId}-${i}.${file.name.split('.').pop()}`;
          const newFile = new File([file], uniqueFileName, { type: file.type });
          newFileList.items.add(newFile);
        }
        
        // Event-Objekt mit modifizierten Dateien erstellen
        const newEvent = {
          ...e,
          target: {
            ...e.target,
            files: newFileList.files,
            form: e.target.form
          }
        };
        
        // Hochladen mit den modifizierten Dateien
        handleImageUpload(newEvent);
        
        // Nach dem Upload Input wieder entfernen
        setTimeout(() => {
          if (window.tempCameraInput) {
            document.body.removeChild(window.tempCameraInput);
            window.tempCameraInput = null;
          }
        }, 1000);
      }
    };
    
    // Klick auf das Eingabefeld simulieren
    tempInput.click();
  };
  
  // Funktion, um den PDF-Rapport herunterzuladen
  const handleDownloadPDF = async () => {
    try {
      // Anzeigen eines Lade-Indikators
      setIsLoading(true);
      
      // Cache-Buster-Parameter hinzufügen
      const timestamp = Date.now();
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL}/reports/${id}/pdf?token=${token}&t=${timestamp}&force=true`;
      
      // Direkter Fetch mit Blob-Response
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });
      
      if (!response.ok) {
        throw new Error(`PDF-Download fehlgeschlagen: ${response.status} ${response.statusText}`);
      }
      
      // Die Antwort als Blob erhalten
      const blob = await response.blob();
      
      // Blob-URL erstellen und für den Download verwenden
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Rapport_${report.project_title || id}.pdf`;
      
      // Link zum DOM hinzufügen, klicken und wieder entfernen
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Fehler beim PDF-Download:', error);
      alert('Der PDF-Download ist fehlgeschlagen. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Neue Funktion zum Aktualisieren des Reparaturstatus
  const handleRepairStatusChange = async (value) => {
    try {
      // API-Aufruf, um nur den Reparaturstatus zu aktualisieren
      await reportsAPI.updateRepairStatus(id, { repair_completed: value });
      
      // Status aktualisieren
      setRepairCompleted(value);
      setSuccess('Reparaturstatus erfolgreich aktualisiert');
      
      // Erfolgsbenachrichtigung nach 3 Sekunden ausblenden
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Reparaturstatus:', err);
      setError('Der Reparaturstatus konnte nicht aktualisiert werden: ' + 
               (err.response?.data?.error || err.message));
    }
  };
  
  // Render-Funktion
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 rounded-full bg-primary-200 mb-4"></div>
          <div className="h-4 w-32 bg-primary-100 rounded"></div>
          <div className="mt-3 text-gray-500">Lade Rapportdaten...</div>
        </div>
      </div>
    );
  }
  
  if (!report) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
          <div className="flex">
            <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
            <p className="text-red-700">Rapport nicht gefunden</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/rapporte')}
          className="flex items-center text-primary-600 hover:text-primary-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zur Übersicht
        </button>
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <h1 className="text-2xl font-bold text-gray-900">{report.title}</h1>
          <div className="mt-2 sm:mt-0">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              report.status === 'draft' ? 'bg-amber-100 text-amber-800' :
              report.status === 'signed' ? 'bg-green-100 text-green-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {report.status === 'draft' ? 'Entwurf' :
               report.status === 'signed' ? 'Unterschrieben' : 'Gesendet'}
            </span>
          </div>
        </div>
        
        <div className="mt-2 text-gray-500">
          <p>Auftrag: {report.project_title}</p>
          <p>Kunde: {report.company_name || `${report.first_name} ${report.last_name}`}</p>
          <p>Erstellt am: {formatDate(report.created_at)}</p>
        </div>
      </div>
      
      {/* Erfolgsmeldung */}
      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
          <div className="flex">
            <Check className="h-6 w-6 text-green-500 mr-3" />
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}
      
      {/* Reparatur abgeschlossen-Sektion */}
      {report.status !== 'sent' && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Reparaturstatus</h2>
          <div className="flex items-center space-x-6">
            <span className="text-gray-700">Reparatur abgeschlossen:</span>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleRepairStatusChange(true)}
                className={`px-4 py-2 rounded-lg flex items-center ${
                  repairCompleted === true 
                    ? 'bg-green-100 text-green-800 border border-green-300' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Check className={`w-4 h-4 mr-2 ${repairCompleted === true ? 'text-green-600' : 'text-gray-400'}`} />
                Ja
              </button>
              <button
                onClick={() => handleRepairStatusChange(false)}
                className={`px-4 py-2 rounded-lg flex items-center ${
                  repairCompleted === false 
                    ? 'bg-red-100 text-red-800 border border-red-300' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <X className={`w-4 h-4 mr-2 ${repairCompleted === false ? 'text-red-600' : 'text-gray-400'}`} />
                Nein
              </button>
              {repairCompleted !== null && (
                <button
                  onClick={() => handleRepairStatusChange(null)}
                  className="text-gray-500 hover:text-gray-700 text-sm underline"
                >
                  Zurücksetzen
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Dieser Status wird im PDF-Rapport in den entsprechenden Checkboxen angezeigt.
          </p>
        </div>
      )}
      
      {/* Fehlermeldung */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
          <div className="flex">
            <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      {/* Zeiterfassungen */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-medium flex items-center">
              <Clock className="w-5 h-5 text-primary-600 mr-2" />
              Zeiterfassungen
            </h2>
            {report.status !== 'sent' && (
              <button 
                onClick={handleAddTimeEntry}
                className="text-primary-600 hover:text-primary-800 flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Zeit hinzufügen
              </button>
            )}
          </div>
          
          {/* Neue Zeiterfassung */}
          {newTimeEntry && (
            <div className="p-4 bg-blue-50 border-b border-gray-200">
              <h3 className="font-medium mb-3">Neue Zeiterfassung</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Startzeit *</label>
                  <input 
                    type="datetime-local"
                    value={newTimeEntry.start_time}
                    onChange={(e) => setNewTimeEntry({...newTimeEntry, start_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Endzeit *</label>
                  <input 
                    type="datetime-local"
                    value={newTimeEntry.end_time}
                    onChange={(e) => setNewTimeEntry({...newTimeEntry, end_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Anzahl Mitarbeiter</label>
                  <input 
                    type="number"
                    min="1"
                    value={newTimeEntry.employee_count}
                    onChange={(e) => setNewTimeEntry({...newTimeEntry, employee_count: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Beschreibung</label>
                  <input 
                    type="text"
                    value={newTimeEntry.description || ''}
                    onChange={(e) => setNewTimeEntry({...newTimeEntry, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Ausgeführte Arbeiten"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-center text-sm text-gray-700">
                    <input 
                      type="checkbox"
                      checked={newTimeEntry.travel_allowance}
                      onChange={(e) => setNewTimeEntry({...newTimeEntry, travel_allowance: e.target.checked})}
                      className="mr-2"
                    />
                    Anfahrtspauschale
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button 
                  onClick={() => setNewTimeEntry(null)}
                  className="px-3 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
                >
                  Abbrechen
                </button>
                <button 
                  onClick={handleSaveNewTimeEntry}
                  className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Speichern
                </button>
              </div>
            </div>
          )}
          
          {/* Zeiterfassungsliste */}
          {timeEntries.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              Keine Zeiterfassungen vorhanden
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Von - Bis</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stunden</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MA</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anfahrt</th>
                    {report.status !== 'sent' && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {timeEntries.map((entry) => (
                    <tr key={entry.id} className={editingTimeEntry?.id === entry.id ? 'bg-blue-50' : ''}>
                      {editingTimeEntry?.id === entry.id ? (
                        <>
                          <td colSpan="5" className="px-4 py-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-700 mb-1">Startzeit</label>
                                <input 
                                  type="datetime-local"
                                  value={editingTimeEntry.start_time}
                                  onChange={(e) => setEditingTimeEntry({...editingTimeEntry, start_time: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-700 mb-1">Endzeit</label>
                                <input 
                                  type="datetime-local"
                                  value={editingTimeEntry.end_time}
                                  onChange={(e) => setEditingTimeEntry({...editingTimeEntry, end_time: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-700 mb-1">Anzahl MA</label>
                                <input 
                                  type="number"
                                  min="1"
                                  value={editingTimeEntry.employee_count}
                                  onChange={(e) => setEditingTimeEntry({...editingTimeEntry, employee_count: parseInt(e.target.value) || 1})}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-700 mb-1">Beschreibung</label>
                                <input 
                                  type="text"
                                  value={editingTimeEntry.description || ''}
                                  onChange={(e) => setEditingTimeEntry({...editingTimeEntry, description: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="flex items-center text-xs text-gray-700">
                                  <input 
                                    type="checkbox"
                                    checked={editingTimeEntry.travel_allowance}
                                    onChange={(e) => setEditingTimeEntry({...editingTimeEntry, travel_allowance: e.target.checked})}
                                    className="mr-1"
                                  />
                                  Anfahrtspauschale
                                </label>
                              </div>
                            </div>
                          </td>
                          <td colSpan="2" className="px-4 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <button 
                                onClick={handleCancelEditTimeEntry}
                                className="text-gray-600 hover:text-gray-800"
                              >
                                Abbrechen
                              </button>
                              <button 
                                onClick={handleSaveTimeEntry}
                                className="text-primary-600 hover:text-primary-800"
                              >
                                Speichern
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 whitespace-nowrap">{formatDate(entry.start_time)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatTime(entry.start_time)} - {formatTime(entry.end_time)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {calculateHours(entry.start_time, entry.end_time)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {entry.employee_count || 1}
                          </td>
                          <td className="px-4 py-3">
                            {entry.description || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {entry.travel_allowance ? 'Ja' : 'Nein'}
                          </td>
                          {report.status !== 'sent' && (
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <button 
                                onClick={() => handleEditTimeEntry(entry)}
                                className="text-primary-600 hover:text-primary-800 mr-2"
                                title="Bearbeiten"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteTimeEntry(entry.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Materialien */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-medium flex items-center">
              <Package className="w-5 h-5 text-primary-600 mr-2" />
              Materialien
            </h2>
            {report.status !== 'sent' && (
              <button 
                onClick={handleAddMaterial}
                className="text-primary-600 hover:text-primary-800 flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Material hinzufügen
              </button>
            )}
          </div>
          
          {/* Neues Material */}
          {newMaterial && (
            <div className="p-4 bg-blue-50 border-b border-gray-200">
              <h3 className="font-medium mb-3">Neues Material</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">Material *</label>
                  <select
                    value={newMaterial.material_id}
                    onChange={(e) => setNewMaterial({...newMaterial, material_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="">-- Material auswählen --</option>
                    {availableMaterials.map(material => (
                      <option key={material.id} value={material.id}>
                        {material.name} ({material.unit} - {formatCurrency(material.price_per_unit)})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Menge *</label>
                  <input 
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={newMaterial.quantity}
                    onChange={(e) => setNewMaterial({...newMaterial, quantity: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Beschreibung</label>
                  <input 
                    type="text"
                    value={newMaterial.description || ''}
                    onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Zusätzliche Information"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button 
                  onClick={() => setNewMaterial(null)}
                  className="px-3 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
                >
                  Abbrechen
                </button>
                <button 
                  onClick={handleSaveNewMaterial}
                  className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center"
                  disabled={!newMaterial.material_id}
                >
                  <Save className="w-4 h-4 mr-1" />
                  Speichern
                </button>
              </div>
            </div>
          )}
          
          {/* Materialliste */}
          {materials.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              Keine Materialien vorhanden
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Einheit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Menge</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preis</th>
                    {report.status !== 'sent' && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {materials.map((material) => (
                    <tr key={material.id} className={editingMaterial?.id === material.id ? 'bg-blue-50' : ''}>
                      {editingMaterial?.id === material.id ? (
                        <>
                          <td colSpan="4" className="px-4 py-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="sm:col-span-2">
                                <label className="block text-xs text-gray-700 mb-1">Material</label>
                                <select
                                  value={editingMaterial.material_id}
                                  onChange={(e) => setEditingMaterial({...editingMaterial, material_id: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                >
                                  {availableMaterials.map(mat => (
                                    <option key={mat.id} value={mat.id}>
                                      {mat.name} ({mat.unit} - {formatCurrency(mat.price_per_unit)})
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-700 mb-1">Menge</label>
                                <input 
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={editingMaterial.quantity}
                                  onChange={(e) => setEditingMaterial({...editingMaterial, quantity: parseFloat(e.target.value) || 0})}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-700 mb-1">Beschreibung</label>
                                <input 
                                  type="text"
                                  value={editingMaterial.description || ''}
                                  onChange={(e) => setEditingMaterial({...editingMaterial, description: e.target.value})}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                            </div>
                          </td>
                          <td colSpan="2" className="px-4 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <button 
                                onClick={handleCancelEditMaterial}
                                className="text-gray-600 hover:text-gray-800"
                              >
                                Abbrechen
                              </button>
                              <button 
                                onClick={handleSaveMaterial}
                                className="text-primary-600 hover:text-primary-800"
                              >
                                Speichern
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3">{material.material_name}</td>
                          <td className="px-4 py-3">{material.unit}</td>
                          <td className="px-4 py-3">{parseFloat(material.quantity).toFixed(2)}</td>
                          <td className="px-4 py-3">{material.description || '-'}</td>
                          <td className="px-4 py-3">
                            {formatCurrency(material.price_per_unit)} pro {material.unit}<br/>
                            <strong>Gesamt: {formatCurrency(material.price_per_unit * material.quantity)}</strong>
                          </td>
                          {report.status !== 'sent' && (
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <button 
                                onClick={() => handleEditMaterial(material)}
                                className="text-primary-600 hover:text-primary-800 mr-2"
                                title="Bearbeiten"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleDeleteMaterial(material.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Bilder-Sektion */}
      {!report.status || report.status === 'draft' || report.status === 'signed' ? (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Camera className="mr-2 h-5 w-5 text-primary-600" />
            Bilder
          </h2>
          
          <div className="bg-white rounded-lg shadow p-4">
            {/* Bild-Upload-Formular */}
            <form className="mb-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Beschreibung (optional)</label>
                <input
                  type="text"
                  name="description"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Beschreibung für die Bilder"
                />
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bilder hochladen</label>
                  <div className="flex items-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      className="hidden"
                      accept="image/*"
                      multiple
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Bilder auswählen
                    </button>
                    <button
                      type="button"
                      onClick={openCamera}
                      className="ml-2 flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Camera className="mr-2 h-4 w-4" />
                      Kamera
                    </button>
                  </div>
                </div>
              </div>
            </form>
            
            {/* Upload-Status */}
            {isUploading && (
              <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded">
                <p className="flex items-center">
                  <span className="mr-2">Bilder werden hochgeladen...</span>
                </p>
              </div>
            )}
            
            {/* Upload-Fehler */}
            {uploadError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded">
                <p>{uploadError}</p>
              </div>
            )}
            
            {/* Vorschau der hochgeladenen Bilder */}
            {uploadedImages.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-medium mb-2">Hochgeladene Bilder</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {uploadedImages.map((image) => (
                    <div key={image.id} className="border rounded-lg overflow-hidden bg-gray-50">
                      <div className="relative h-40">
                        <img 
                          src={image.url} 
                          alt="Vorschau" 
                          className="h-full w-full object-cover"
                        />
                        {image.isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 text-white">
                            <span>Wird hochgeladen...</span>
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-sm">{image.file.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Bereits hochgeladene Bilder */}
            {images.length > 0 ? (
              <div>
                <h3 className="text-lg font-medium mb-2">Gespeicherte Bilder</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {images.map((image) => (
                    <div key={image.id} className="border rounded-lg overflow-hidden bg-white shadow">
                      <div className="relative h-40">
                        <img 
                          src={`${process.env.REACT_APP_API_URL}${image.path}`}
                          alt={image.description || 'Rapport-Bild'} 
                          className="h-full w-full object-cover"
                        />
                        <button
                          onClick={() => handleDeleteImage(image.id)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          title="Bild löschen"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="p-3">
                        <input
                          type="text"
                          value={image.description || ''}
                          onChange={(e) => {
                            // Lokale Kopie der Bilder aktualisieren
                            const newImages = images.map(img => 
                              img.id === image.id 
                                ? { ...img, description: e.target.value } 
                                : img
                            );
                            setImages(newImages);
                          }}
                          onBlur={(e) => handleUpdateDescription(image.id, e.target.value)}
                          className="w-full text-sm border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Bildbeschreibung hinzufügen"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(image.upload_date).toLocaleString('de-DE')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Keine Bilder für diesen Rapport</p>
            )}
          </div>
        </div>
      ) : (
        images.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Camera className="mr-2 h-5 w-5 text-primary-600" />
              Bilder
            </h2>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {images.map((image) => (
                  <div key={image.id} className="border rounded-lg overflow-hidden">
                    <div className="relative h-40">
                      <img 
                        src={`${process.env.REACT_APP_API_URL}${image.path}`}
                        alt={image.description || 'Rapport-Bild'} 
                        className="h-full w-full object-cover"
                      />
                    </div>
                    {image.description && (
                      <div className="p-2">
                        <p className="text-sm">{image.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(image.upload_date).toLocaleString('de-DE')}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      )}
      
      {/* PDF-Link und Aktionen */}
      <div className="mt-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleDownloadPDF();
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center"
              disabled={isLoading}
            >
              {isLoading ? 'Wird geladen...' : (
                <>
                  <FileText className="mr-2" />
                  PDF anzeigen
                </>
              )}
            </a>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/rapporte')}
              className="border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-lg"
            >
              Zurück
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetailPage;
